import { Button, DataTableV2, Text } from "components/commons";
import styles from "../purchase-order.module.scss";
import locale from "localization";
import { useFormContext } from "contexts/form-context";
import useDataTable from "hooks/useDataTable";
import { Add } from "@material-ui/icons";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useAlert, useDynamicFieldsForm, useModal, useMount, useApi } from "hooks";
import { SelectProductsModal } from "components/modals";
import Validation from "utils/validation.utils";
import useProducts from "hooks/useProducts";
import { mapDataToListFuel } from "./mapper";
import { fuelColumns, nonFuelColumns } from "./columns";
import { StationContext } from "contexts";
import { formatVolume } from "utils";
import ChangeTypeModal from "./change-type-modal";
import { CircularProgress } from "@mui/material";
import { ShiftStatus } from "enums";
import { getShiftByShiftId } from "apis/shift-records.api";
import Big from "big.js";
import { sumBy } from "lodash";
import { prettifyProductCode } from "utils/pretty.utils";

const PurchaseOrderProducts = ({ shiftsRecord, formMode }) => {
  const prevProductsRef = useRef(null);
  const productMappingsRef = useRef({});
  const { shifts } = shiftsRecord || {};
  const { station } = useContext(StationContext);
  const { addAlert } = useAlert();
  const {
    allProducts,
    fetch: getAllProducts,
    loading: isLoadingGetAllProducts,
  } = useProducts({ withStationCode: true });
  const {
    fields: rootFields,
    modifyField: modifyRootField,
    setSubFormsSubmittableState,
    initialState,
  } = useFormContext();

  const { request: getShiftById } = useApi({
    api: getShiftByShiftId,
  });

  const { fieldsAsRows, addRows, rowsValues, removeRow, modifyField, modifyForm, clearRows } =
    useDynamicFieldsForm({
      rowId: "sku",
      onChangeIsSubmittable: ({ isFormSubmittable, rowsValues }) => {
        setSubFormsSubmittableState((f) => ({ ...f, products: isFormSubmittable }));
        modifyRootField("products", {
          value: rowsValues,
        });
      },
    });

  const isFuelOnly = useMemo(
    () => rootFields?.productType?.value === "fuel",
    [rootFields?.productType?.value],
  );

  const filteredByProductTypeAllProducts = useMemo(() => {
    return allProducts.filter((p) => {
      if (isFuelOnly) {
        return p.type === "fuel";
      } else {
        return p.type !== "fuel";
      }
    });
  }, [allProducts, isFuelOnly]);

  const productsOptions = useMemo(() => {
    const selected = rowsValues.map((p) => p.sku);
    return filteredByProductTypeAllProducts
      .filter((p) => p.type === rootFields?.productType?.value)
      .filter((p) => !selected.includes(p.sku))
      .map((p) => ({
        label: p.name,
        value: p.sku,
      }));
  }, [filteredByProductTypeAllProducts, rootFields?.productType?.value, rowsValues]);

  const onRemove = (rowIndex) => {
    removeRow(rowIndex);
    return addAlert({
      title: "Product Removed",
      subtitle: "You have successfully removed the product(s)",
      type: "success",
      interval: 3000,
    });
  };

  const changeProductTypeModal = useModal();
  const selectProductModal = useModal();

  const modifiedFuelColumns = [...fuelColumns];
  const modifiedNonFuelColumns = [...nonFuelColumns];

  const { table } = useDataTable({
    value: rowsValues || [],
    filter: { initialState: {}, mapper: (param) => param },
    table: {
      key: "products",
      mapper: (products) => {
        if (isFuelOnly) {
          return mapDataToListFuel({
            products,
            productsOptions,
            modifyForm,
            modifyField,
            onRemove,
            fieldsAsRows,
            allProducts,
            formMode,
          });
        }
      },
      columns: isFuelOnly ? modifiedFuelColumns : modifiedNonFuelColumns,
    },
  });

  const handleProductEntries = useCallback(
    async (selectedProducts) => {
      selectProductModal.close();
      const stationUgts = station?.stationUgts;
      const rows = selectedProducts.map((product) => {
        const maxLiter = stationUgts?.find((ugt) => ugt.productCode === product.sku)?.liter;

        // Get latest inventory values from productMappingsRef if available
        let inventoryLevelCm = product?.inventoryLevelCm;
        let inventoryLevelLiter = product?.inventoryLevelLiter;
        let totalizerValue = 0;

        // If not available, try to get from productMappingsRef (from shift data)
        const mappings = productMappingsRef.current;
        let isInventoryFromTotalizer = false;
        if (mappings && mappings[product.sku]) {
          if (!inventoryLevelCm || !inventoryLevelLiter) {
            inventoryLevelCm = mappings[product.sku].cm;
            inventoryLevelLiter = formatVolume(mappings[product.sku].liter);
          }
          isInventoryFromTotalizer = mappings[product.sku].isInventoryFromTotalizer;
          totalizerValue = mappings[product.sku].totalizerValue;
        }

        if (isFuelOnly) {
          return [
            {
              name: "sku",
              value: product.sku,
              required: true,
              validations: [Validation.required({ noMessage: true })],
            },
            {
              name: "product",
              value: product.name,
              required: false,
              validations: [],
            },
            {
              name: "inventoryLevelCm",
              value: inventoryLevelCm || "",
              required: true,
              decimalPlaces: 4,
              isPriceValue: true,
              max: 99999.9999,
              validations: [Validation.required({ noMessage: true })],
            },
            {
              name: "inventoryLevelLiter",
              value: inventoryLevelLiter || "",
              required: true,
              decimalPlaces: 3,
              isPriceValue: true,
              min: 0.0,
              max: 100000,
              validations: [
                Validation.required({ noMessage: true }),
                Validation.min(0.001, {
                  customMessage: (
                    <locale.Populate text={locale.valueShouldNotBe} items={["0.000"]} />
                  ),
                }),
                Validation.max(maxLiter, {
                  customMessage: `Maximum UGT limit is ${formatVolume(maxLiter)}`,
                }),
              ],
            },
            {
              name: "volume",
              value: product?.volume || "",
              required: true,
              decimalPlaces: 3,
              isPriceValue: true,
              min: 0.0,
              max: 100000,
              validations: [
                Validation.required({ noMessage: true }),
                Validation.min(0.001, {
                  customMessage: (
                    <locale.Populate text={locale.valueShouldNotBe} items={["0.000"]} />
                  ),
                }),
                Validation.max(maxLiter, {
                  customMessage:
                    "Total volume exceeds the UGT capacity and may cause an overflow. Please reduce the quantity.",
                }),
              ],
            },
            {
              name: "isInventoryFromTotalizer",
              value: isInventoryFromTotalizer,
            },
            {
              name: "totalizerValue",
              value: totalizerValue,
            },
          ];
        }
      });

      addRows(rows);
    },
    [addRows, isFuelOnly, selectProductModal, station?.stationUgts],
  );

  const showProductSelection = useCallback(() => {
    selectProductModal.show({
      title: locale.selectProducts,
      primary: {
        text: fieldsAsRows.length > 0 ? locale.save : locale.add,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectProductModal]);

  useMount(async () => {
    await getAllProducts();
  });

  // set default value
  useEffect(() => {
    const products = initialState?.products?.value;
    if (products?.length > 0 && !isLoadingGetAllProducts) {
      const defaultValue = products
        ?.map((productDelivery) => {
          const product = allProducts?.find((p) => p.sku === productDelivery.product);
          if (!product) return null;
          return {
            ...product,
            ...productDelivery,
          };
        })
        ?.filter((p) => !!p);

      if (defaultValue?.length > 0) {
        handleProductEntries(defaultValue);
      } else {
        modifyRootField("products", {
          value: [],
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProducts, initialState?.products?.value, isLoadingGetAllProducts]);

  useEffect(() => {
    const isEmpty = rootFields?.products?.value?.length === 0;
    if (isEmpty) {
      clearRows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootFields?.products?.value]);

  useEffect(() => {
    const fetchShift = async () => {
      if (!shifts?.length) return;

      const mostRecentShift = await getShiftById({ shiftId: shifts[0]?.shiftId });
      if (!mostRecentShift) {
        return;
      }

      // Compute inventory based on totalizer readings and status
      const computeTotalizer = (code) => {
        const productKey = code.charAt(0).toUpperCase() + code.slice(1);
        // Prefer recentShift ending values if available
        const recentEnding = mostRecentShift?.recentShift?.[`ending${productKey}`];
        let endingInventory;
        if (recentEnding !== undefined && recentEnding !== null) {
          endingInventory = Number(recentEnding);
        } else {
          const transactionItems =
            mostRecentShift?.transactionItem?.[`${code}TransactionItem`] || [];

          // Get liters sold from totalizer
          const litersSoldPerTotalizer = Math.abs(
            Big(sumBy(transactionItems, "endingTotalizer") || 0).minus(
              sumBy(transactionItems, "beginningTotalizer") || 0,
            ),
          );

          // Get calibration and pump error
          const calibration =
            mostRecentShift?.transactionPaymentMethod?.pumpCalibration?.find(
              (o) =>
                o.name?.toLowerCase() ===
                (code === "gas91"
                  ? "gas 91"
                  : code === "gas95"
                    ? "gas 95"
                    : code === "gas97"
                      ? "gas 97"
                      : code),
            )?.quantity || 0;

          const pumpError =
            mostRecentShift?.transactionPaymentMethod?.pumpError?.find(
              (o) =>
                o.name?.toLowerCase() ===
                (code === "gas91"
                  ? "gas 91"
                  : code === "gas95"
                    ? "gas 95"
                    : code === "gas97"
                      ? "gas 97"
                      : code),
            )?.quantity || 0;

          // Get liters available for sale (beginning + delivery)
          const beginning = mostRecentShift?.shift?.[`beginning${productKey}`] || 0;
          const delivery = mostRecentShift?.delivery?.[`total${productKey}Delivered`] || 0;
          const litersAvailableForSale = Number(Big(beginning).add(delivery));

          // Compute ending inventory per totalizer
          endingInventory = Math.abs(
            litersAvailableForSale - (litersSoldPerTotalizer - calibration - pumpError),
          );
        }

        return {
          cm: 0,
          liter: endingInventory,
          totalizerValue: endingInventory,
        };
      };

      // Determine which inventory to use based on status and dipstick
      let getInventory = (code) => ({ cm: 0, liter: 0, isInventoryFromTotalizer: false });
      if (mostRecentShift?.shift?.status === ShiftStatus.Closed) {
        if (mostRecentShift?.shift?.isReadingDipstickEnding) {
          // Case 1: Closed with Dipstick Reading
          getInventory = (code) => ({
            cm: mostRecentShift?.shift?.[`ending${code.charAt(0).toUpperCase() + code.slice(1)}Cm`],
            liter:
              mostRecentShift?.shift?.[`ending${code.charAt(0).toUpperCase() + code.slice(1)}`],
            isInventoryFromTotalizer: false,
          });
        } else {
          // Case 3: Closed without Dipstick Reading
          getInventory = (code) => {
            const inv = computeTotalizer(code);
            return { ...inv, isInventoryFromTotalizer: true };
          };
        }
      } else if (mostRecentShift?.shift?.status === ShiftStatus.Ended) {
        // Case 2: Ended - Use Ending Totalizer
        getInventory = (code) => {
          const inv = computeTotalizer(code);
          return { ...inv, isInventoryFromTotalizer: true };
        };
      }

      const productCodes = ["diesel", "gas91", "gas95", "gas97"];
      const productMappings = {};
      productCodes.forEach((code) => {
        const inv = getInventory(code);
        productMappings[code] = {
          name: prettifyProductCode(code),
          productCode: code,
          cm: inv.cm,
          liter: inv.liter,
          isInventoryFromTotalizer: inv.isInventoryFromTotalizer,
          totalizerValue: inv.totalizerValue,
        };
      });
      // Store productMappings in ref for use elsewhere
      productMappingsRef.current = productMappings;

      // Create initial products if rootFields.products.value is empty
      const products = rootFields.products.value || [];
      const prevProducts = prevProductsRef.current;

      // If products array is empty, create initial products from productMappings
      const productsToUpdate =
        products.length === 0
          ? Object.entries(productMappings).map(([code, data]) => ({
              sku: code,
              name: data?.name,
              product: data.productCode,
              productCode: data.productCode,
              inventoryLevelCm: data.cm ?? 0,
              inventoryLevelLiter: data.liter ?? 0,
              volume: 0,
              isInventoryFromTotalizer: data.isInventoryFromTotalizer,
              totalizerValue: data.totalizerValue || 0,
            }))
          : products;

      // Only update if products have changed
      if (JSON.stringify(productsToUpdate) === JSON.stringify(prevProducts)) return;
      prevProductsRef.current = productsToUpdate;

      // Update existing products with new inventory values
      const updatedProducts = productsToUpdate.map((product) => {
        const productCode = product.sku?.toLowerCase();
        const shiftData = productMappings[productCode];
        if (shiftData) {
          return {
            ...product,
            name: shiftData.name,
            product: shiftData.productCode,
            productCode: shiftData.productCode,
            inventoryLevelCm: product?.inventoryLevelCm ? product?.inventoryLevelCm : shiftData.cm,
            inventoryLevelLiter: product?.inventoryLevelLiter
              ? formatVolume(product?.inventoryLevelLiter)
              : formatVolume(shiftData.liter),
            isInventoryFromTotalizer:
              product?.isInventoryFromTotalizer ?? shiftData.isInventoryFromTotalizer,
            totalizerValue: shiftData.totalizerValue,
            volume: 0,
          };
        }

        return product;
      });

      modifyRootField("products", { value: [...updatedProducts] });
      handleProductEntries(updatedProducts);
    };

    fetchShift();
  }, [shifts]);

  return (
    <>
      <Text strong color={"black"} className={styles.sectionTitle}>
        {locale.itemOrder}
      </Text>

      {isLoadingGetAllProducts && <CircularProgress size={20} />}
      {rootFields?.productType?.value && (
        <>
          <DataTableV2
            {...table}
            size="small"
            hidePagination
            hideNoSearchResultRender
            tableVersion="v2"
            striped={true}
            className={styles.tableContainer}
            // loading={isGettingShift}
          />
          <br />
          <Button
            primary
            className={styles.palePurpleButton}
            disabled={isLoadingGetAllProducts || !rootFields?.productType?.value}
            onClick={showProductSelection}
            loading={isLoadingGetAllProducts}
          >
            <div className={styles.btn}>
              <Add fontSize="small" /> {locale.addProduct}
            </div>
          </Button>
        </>
      )}
      <SelectProductsModal
        {...selectProductModal}
        value={rowsValues.map((p) => p.sku)}
        onChange={handleProductEntries}
        coloredProduct={false}
        allProducts={filteredByProductTypeAllProducts}
        excludeProductType={isFuelOnly ? ["lubes", "lpg"] : ["fuel"]}
        excludeCols={isFuelOnly ? ["category", "variant"] : []}
        showFilter={!isFuelOnly}
        className={isFuelOnly && styles.productModal}
        dataTableProps={
          isFuelOnly
            ? {
                hidePagination: true,
              }
            : {}
        }
        filterProps={{
          hiddenInputs: {
            productType: true,
          },
        }}
        required
      />
      <ChangeTypeModal {...changeProductTypeModal} />
    </>
  );
};

export default PurchaseOrderProducts;
